/**
 * ParserStatsPanel component displays parsing statistics.
 * Shows valid/invalid line counts, limit information, and warnings.
 *
 * @param {Object} props - Component props
 * @param {Object} props.stats - Parser statistics
 * @param {number} props.stats.totalLines - Total non-empty lines
 * @param {number} props.stats.validCount - Valid flashcards count
 * @param {number} props.stats.invalidCount - Invalid lines count
 * @param {number} props.stats.batchLimit - Maximum per-batch limit (40)
 * @param {number} props.stats.globalLimit - Maximum total limit (100)
 * @param {number} props.stats.currentCount - Current flashcard count
 * @param {number} props.stats.availableSlots - Remaining slots
 * @param {boolean} props.stats.limitReached - Whether batch limit was exceeded
 * @param {boolean} props.stats.globalLimitReached - Whether global limit is reached
 * @returns {React.ReactElement} Parser stats panel
 */
export default function ParserStatsPanel({ stats }) {
  const {
    validCount,
    invalidCount,
    batchLimit,
    availableSlots,
    limitReached,
    globalLimitReached,
  } = stats

  return (
    <div className="parser-stats">
      <div className="parser-stats__counts">
        <div className="parser-stats__item parser-stats__item--valid">
          <span className="parser-stats__label">Poprawne:</span>
          <span className="parser-stats__value">{validCount}</span>
        </div>

        {invalidCount > 0 && (
          <div className="parser-stats__item parser-stats__item--invalid">
            <span className="parser-stats__label">Pominięte:</span>
            <span className="parser-stats__value">{invalidCount}</span>
          </div>
        )}
      </div>

      {/* Global limit reached warning */}
      {globalLimitReached && (
        <div className="parser-stats__warning" role="alert">
          <span className="parser-stats__warning-icon" aria-hidden="true">
            🚫
          </span>
          <p className="parser-stats__warning-text">
            Osiągnięto maksymalny limit 100 fiszek. Usuń niektóre fiszki, aby
            dodać nowe.
          </p>
        </div>
      )}

      {/* Batch limit warning (when not at global limit) */}
      {!globalLimitReached && limitReached && (
        <div className="parser-stats__warning" role="status">
          <span className="parser-stats__warning-icon" aria-hidden="true">
            ⚠
          </span>
          <p className="parser-stats__warning-text">
            Limit {Math.min(batchLimit, availableSlots)} fiszek został
            osiągnięty w tym imporcie.
            {availableSlots < batchLimit && (
              <>
                {' '}
                Pozostało miejsce na jeszcze {availableSlots}{' '}
                {availableSlots === 1
                  ? 'fiszkę'
                  : availableSlots % 10 >= 2 &&
                      availableSlots % 10 <= 4 &&
                      (availableSlots % 100 < 10 || availableSlots % 100 >= 20)
                    ? 'fiszki'
                    : 'fiszek'}
                .
              </>
            )}
          </p>
        </div>
      )}

      {/* Available slots info (when approaching limit) */}
      {!globalLimitReached && availableSlots <= 20 && validCount > 0 && (
        <div className="parser-stats__info" role="status">
          <p className="parser-stats__info-text">
            Pozostało miejsce na {availableSlots}{' '}
            {availableSlots === 1
              ? 'fiszkę'
              : availableSlots % 10 >= 2 &&
                  availableSlots % 10 <= 4 &&
                  (availableSlots % 100 < 10 || availableSlots % 100 >= 20)
                ? 'fiszki'
                : 'fiszek'}
            .
          </p>
        </div>
      )}

      {/* Progress bar when not at global limit */}
      {!globalLimitReached && validCount > 0 && (
        <div className="parser-stats__progress">
          <div
            className="parser-stats__progress-bar"
            style={{
              width: `${(validCount / Math.min(batchLimit, availableSlots)) * 100}%`,
            }}
            aria-hidden="true"
          ></div>
          <span className="parser-stats__progress-label">
            {validCount} / {Math.min(batchLimit, availableSlots)}
          </span>
        </div>
      )}
    </div>
  )
}
